import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw, Check, Eye, History, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";

interface TemplateVersioningProps {
  templateId: string | null;
}

interface VersionDiffViewerProps {
  oldVersion: any;
  newVersion: any;
}

const VersionDiffViewer = ({ oldVersion, newVersion }: VersionDiffViewerProps) => {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <h3 className="text-sm font-medium mb-2">Previous Version</h3>
        <pre className="bg-muted p-4 rounded-lg overflow-auto max-h-[400px] text-sm">
          {oldVersion.content_structure.template}
        </pre>
      </div>
      <div>
        <h3 className="text-sm font-medium mb-2">Current Version</h3>
        <pre className="bg-muted p-4 rounded-lg overflow-auto max-h-[400px] text-sm">
          {newVersion.content_structure.template}
        </pre>
      </div>
    </div>
  );
};

export const TemplateVersioning = ({ templateId }: TemplateVersioningProps) => {
  const queryClient = useQueryClient();
  const [selectedVersions, setSelectedVersions] = useState<[any, any] | null>(null);
  const [previewVersion, setPreviewVersion] = useState<any | null>(null);

  const { data: versions, isLoading } = useQuery({
    queryKey: ['template-versions', templateId],
    queryFn: async () => {
      if (!templateId) return [];
      const { data, error } = await supabase
        .from('newsletter_template_versions')
        .select('*')
        .eq('template_id', templateId)
        .order('version_number', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!templateId
  });

  const handleRollback = async (versionId: string) => {
    try {
      const { data: version, error: versionError } = await supabase
        .from('newsletter_template_versions')
        .select('content_structure')
        .eq('id', versionId)
        .single();

      if (versionError) throw versionError;

      const { error: updateError } = await supabase
        .from('newsletter_templates')
        .update({ content_structure: version.content_structure })
        .eq('id', templateId!);

      if (updateError) throw updateError;

      toast.success("Template rolled back successfully");
      queryClient.invalidateQueries({ queryKey: ['newsletter-templates'] });
    } catch (error) {
      console.error('Error rolling back version:', error);
      toast.error("Failed to roll back version");
    }
  };

  const compareVersions = (version1: any, version2: any) => {
    setSelectedVersions([version1, version2]);
  };

  if (!templateId) {
    return (
      <div className="flex items-center justify-center h-[400px] text-muted-foreground">
        Select a template to view versions
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[400px] text-muted-foreground">
        Loading versions...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4">
        {versions?.map((version, index) => (
          <Card key={version.id}>
            <CardContent className="p-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-medium">Version {version.version_number}</h3>
                  <p className="text-sm text-muted-foreground">
                    Created: {format(new Date(version.created_at), 'PPpp')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPreviewVersion(version)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Preview
                  </Button>
                  {index < versions.length - 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => compareVersions(versions[index + 1], version)}
                    >
                      <History className="h-4 w-4 mr-2" />
                      Compare
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRollback(version.id)}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Rollback
                  </Button>
                  {version.is_winner && (
                    <Badge variant="secondary" className="bg-green-100 text-green-800">
                      <Check className="h-4 w-4 mr-2" />
                      Active Version
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!selectedVersions} onOpenChange={() => setSelectedVersions(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Compare Versions</DialogTitle>
          </DialogHeader>
          {selectedVersions && (
            <VersionDiffViewer
              oldVersion={selectedVersions[0]}
              newVersion={selectedVersions[1]}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewVersion} onOpenChange={() => setPreviewVersion(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Version Preview</DialogTitle>
          </DialogHeader>
          {previewVersion && (
            <div className="prose dark:prose-invert max-w-none">
              <div dangerouslySetInnerHTML={{ __html: previewVersion.content_structure.template }} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};